import loadable from '@loadable/component';
import { type FunctionComponent, useContext } from 'react';
import { PRODUCT_CONFIG_SECTION_ID } from '../../../../constants/product';
import { ProductFamilyContext } from '../../../../contexts/product-family/product-family.context';
import { ProductPricedOptionsContext } from '../../../../contexts/product-priced-options/product-priced-options.context';
import { VariantContext } from '../../../../contexts/variant/variant.context';
import { ANALYTICS_PAGE_NAMES } from '../../../../helpers/analytics/analytics.helper';
import { pluralize } from '../../../../helpers/general-helper/general-helper';
import { calculatePricedOptionsSubtotal } from '../../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import { mapVariationGroupsToSelectedVariant } from '../../../../helpers/product-helper/variations/variations.helper';
import { useSiteViewPreference } from '../../../../hooks/apollo/employee/employee.hooks';
import { AddSampleToCartButton } from '../../add-sample-to-cart-button/add-sample-to-cart-button.component';
import { ProductPrice } from '../../product-price/product-price.component';
import { ShippingEligibility } from '../../shipping-eligibility/shipping-eligibility.component';
import { ProductAddToCartSection } from '../product-add-to-cart-section/product-add-to-cart-section.component';

const LoadableInternalToolsPanel = loadable(
	() =>
		import(
			/* webpackChunkName: "internalproductinformation" */ '../../../../components/product-components/internal-tools-panel/internal-tools-panel.component'
		),
	{
		resolveComponent: ({ InternalToolsPanel }) => InternalToolsPanel
	}
);

const LoadableVariationGroup = loadable(
	() => import(/* webpackChunkName: "variationgroup" */ '../product-variation-group/product-variation-group.component'),
	{
		resolveComponent: ({ ProductVariationGroup }) => ProductVariationGroup
	}
);

const LoadablePricedOptionsGroup = loadable(
	() => import(/* webpackChunkName: "pricedoptionsgroup" */ '../priced-options-group/priced-options-group.component'),
	{
		resolveComponent: ({ PricedOptionsGroup }) => PricedOptionsGroup
	}
);

export const PdpControls: FunctionComponent = () => {
	const { showAsEmployee: isEmployee } = useSiteViewPreference();
	const { currentVariant, selectedVariant } = useContext(VariantContext);
	const { productFamily } = useContext(ProductFamilyContext);
	const { mainItemQuantity, subItems, selectedOptions } = useContext(ProductPricedOptionsContext);

	if (!productFamily || !selectedVariant) {
		return null;
	}

	const pricedOptionsGroups = 'productOptionGroups' in productFamily ? productFamily.productOptionGroups : [];

	const rawVariationGroups = 'variationGroups' in productFamily ? productFamily.variationGroups : [];

	// Map variation groups to show the correct variant data based on the selected variant
	const variationGroups = mapVariationGroupsToSelectedVariant(rawVariationGroups, selectedVariant.title);

	const { totalItems: currentTotalItems, totalCost: currentTotalCost } = calculatePricedOptionsSubtotal(
		currentVariant.priceInfo.current,
		selectedOptions,
		subItems,
		mainItemQuantity
	);

	const itemCountText = currentTotalItems > 0 ? ` (${currentTotalItems}  ${pluralize('item', currentTotalItems)}):` : ':';
	const subtotalLabel = `Subtotal${itemCountText}`;

	const productStatusPending = currentVariant.status === 'pending';

	return (
		<div data-testid="pdp-controls" id={PRODUCT_CONFIG_SECTION_ID} className="dn-p">
			{variationGroups.length > 0 &&
				variationGroups
					.filter((group) => group.options.length > 0)
					.map(({ name, help, options }, index) => (
						<LoadableVariationGroup
							key={name + index}
							name={name}
							helpText={help?.description}
							helpImage={help?.image || undefined}
							options={options}
						/>
					))}
			{pricedOptionsGroups.length > 0 && (
				<div>
					{pricedOptionsGroups.map(({ title, helpTitle, helpText, pricedOptions }, index) => (
						<LoadablePricedOptionsGroup
							key={title + index}
							name={title}
							helpTitle={helpTitle || ''}
							helpText={helpText || ''}
							options={pricedOptions}
							groupIndex={index}
						/>
					))}
					<strong className="flex items-center f4 mt3">
						{`${subtotalLabel} `}
						<ProductPrice current={currentTotalCost} className="ml2" />
					</strong>
				</div>
			)}

			<div className={'mt2 mt4-ns'}>
				<ShippingEligibility showOrderedBy={true} showZipCodeChanger={true} />
			</div>

			{isEmployee && (
				<div className="dn db-ns mt2">
					<LoadableInternalToolsPanel
						modelNumber={currentVariant.modelNumber}
						productShowroomCode={currentVariant.productShowroomCode}
					/>
				</div>
			)}

			{currentVariant.sampleId && (
				<div className={'mb2 mt3'}>
					<AddSampleToCartButton pageName={ANALYTICS_PAGE_NAMES.PRODUCT_DETAILS_PAGE} sampleId={currentVariant.sampleId} />
				</div>
			)}
			{productStatusPending ? (
				<div className="theme-error f4 mb3 mt3">
					This product is pending addition to the site, please reach out to the brand manager for more info.
				</div>
			) : (
				<ProductAddToCartSection />
			)}
		</div>
	);
};
